import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const LINKS = [
    { label: "WORK", target: "#work", testid: "nav-link-work" },
    { label: "REEL", target: "reel", testid: "nav-link-reel" },
    { label: "ABOUT", target: "#about", testid: "nav-link-about" },
    { label: "GAMES", target: "/games", testid: "nav-link-games" },
    { label: "CONTACT", target: "#contact", testid: "nav-link-contact" },
];

export const LogoMark = ({ size = 20 }) => (
    <img
        src="/images/logo-shield.png"
        alt="EXODUS FX logo"
        style={{ height: size, width: "auto" }}
    />
);

export default function Nav({ onNavigate, onPlayReel }) {
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();
    const routerNavigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const handle = (e, link) => {
        e.preventDefault();
        if (link.target === "reel") {
            onPlayReel();
        } else if (link.target.startsWith("/")) {
            routerNavigate(link.target);
        } else if (location.pathname !== "/") {
            routerNavigate("/");
            setTimeout(() => onNavigate(link.target), 400);
        } else {
            onNavigate(link.target);
        }
    };

    return (
        <motion.header
            data-testid="header-nav"
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
                scrolled ? "bg-[#0a0a0c]/80 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
            }`}
        >
            <div className="flex items-center justify-between px-5 sm:px-10 py-4">
                <a
                    href="#top"
                    data-testid="nav-logo"
                    onClick={(e) => {
                        e.preventDefault();
                        if (location.pathname !== "/") routerNavigate("/");
                        else onNavigate(0);
                    }}
                    className="flex items-center gap-3 group"
                >
                    <LogoMark size={30} />
                    <span className="hidden min-[420px]:inline font-mono-x text-[11px] tracking-[0.35em] text-neutral-300 group-hover:text-white transition-colors whitespace-nowrap">
                        EXODUS FX
                    </span>
                </a>
                <nav className="flex items-center gap-4 sm:gap-9">
                    {LINKS.map((link) => (
                        <a
                            key={link.label}
                            href={link.target === "reel" ? "#reel" : link.target}
                            data-testid={link.testid}
                            onClick={(e) => handle(e, link)}
                            className="group relative font-mono-x text-[10px] sm:text-[11px] tracking-[0.3em] text-neutral-400 hover:text-[#FF3333] transition-colors duration-300"
                        >
                            {link.label}
                            <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#FF3333] transition-all duration-300 group-hover:w-full" aria-hidden="true" />
                        </a>
                    ))}
                    <button
                        data-testid="nav-cta-watch-reel"
                        onClick={onPlayReel}
                        className="hidden sm:flex items-center gap-2 border border-white/15 px-4 py-2 transition-all duration-300 hover:border-[#FF3333] hover:bg-[#FF3333] hover:shadow-[0_0_24px_rgba(255,51,51,0.35)] active:scale-95"
                    >
                        <Play size={10} fill="currentColor" />
                        <span className="font-mono-x text-[10px] tracking-[0.3em]">PLAY</span>
                    </button>
                </nav>
            </div>
        </motion.header>
    );
}
